import { useEffect, useState, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import DOMPurify from 'isomorphic-dompurify';
import { motion } from 'framer-motion';
import formatDate from '../../../lib/dateFormat';
import { cn } from '../../../lib/utils';
import MessageFactory from '../MessageFactory';
import ThreeDotLoader from '../../Loader/ThreeDotLoader';
import useChatStore from '../../../store';
import getTextColor from '../../../hooks/getTextColorContrast';
import { useThemeQuery } from '../../../reactQuery/get';
import { MessageData } from '../../../types';
import MessageContent from '../MessageContent';
import MessageTimestamp from '../MessageTimestamp';
import { MessageImagesProvider } from '../../../contexts/MessageImagesContext';

const DEFAULT_PRIMARY_COLOR = '#FF6F98';
const DEFAULT_SECONDARY_COLOR = '#FFE8ED';

function TextMessage({
  text,
  data,
  created_at,
  seen,
  received,
  role,
  loading,
  hasError,
  isLastMessage,
}: MessageData) {
  const { setIsLastMessageRerendered } = useChatStore((state) => state);
  const [displayResponse, setDisplayResponse] = useState<string>('');
  // State to track which messages have had their images displayed
  const [shownImages, setShownImages] = useState<Set<string>>(new Set());
  const [isLastMessageDisplayed, setIsLastMessageDisplayed] = useState(false);

  const isUser = role === 'user';
  const allowedAttributes = { ALLOWED_ATTR: ['href', 'target'] };
  const { organization_id: organizationId, project_id: projectId, chat_id: chatId } = useParams();

  const formattedText = text?.replace(/\n/g, '<br>');
  const sanitizedFormattedText = DOMPurify.sanitize(formattedText || '');
  const sanitizedDisplayResponse = DOMPurify.sanitize(displayResponse || '', allowedAttributes);

  const projectThemeId = organizationId ? projectId : chatId;
  const { data: themeData } = useThemeQuery(projectThemeId);

  const backgroundColor = useMemo(
    () =>
      isUser
        ? themeData?.['chat-primary'] || DEFAULT_PRIMARY_COLOR
        : themeData?.['chat-secondary'] || DEFAULT_SECONDARY_COLOR,
    [isUser, themeData],
  );

  const textColor = useMemo(() => getTextColor(backgroundColor || '#FFFFFF'), [backgroundColor]);
  // Update shownImages state when data changes, displaying images immediately if they exist
  useEffect(() => {
    // Store file_id of images that have been shown
    if (data && data.length > 0) {
      const fileIds = data
        .map((item) => item.file_id)
        .filter((id): id is string => id !== undefined);
      setShownImages(new Set(fileIds));
    }
  }, [data]);

  // Display the message text immediately with growing bubble animation
  useEffect(() => {
    if (!formattedText?.length || !isLastMessage) {
      return;
    }

    // Set the full text immediately
    setDisplayResponse(formattedText);
    setIsLastMessageDisplayed(true);
    setIsLastMessageRerendered(String(formattedText.length));
  }, [formattedText, isLastMessage, setIsLastMessageRerendered]);

  // Function to render messages (both user and non-user) conditionally
  const renderMessages = () => {
    if (!data || data.length === 0) return null;

    // Filter the data to only include items that should be rendered
    const itemsToRender = data.filter((item) => {
      const fileId = item.file_id || item.url;
      return isUser || (fileId && shownImages.has(fileId));
    });

    if (itemsToRender.length === 0) return null;

    // Separate PDFs from images/other media
    const pdfItems = itemsToRender.filter((item) => item.type === 'application');
    const nonPdfItems = itemsToRender.filter((item) => item.type !== 'application');
    const nonPdfItemsLength = nonPdfItems.length;

    // Extract all image objects with URLs and labels for the carousel
    const imageItems = nonPdfItems
      .filter((item) => item.type === 'image' || item.type === 'video')
      .map((item) => ({
        url: item.url,
        label: item.label,
      }));

    // If there's only one media item (and no PDFs), render it directly
    if (itemsToRender.length === 1 && pdfItems.length === 0) {
      const item = nonPdfItems[0];
      const fileId = item.file_id || item.url;
      return (
        <MessageImagesProvider images={imageItems}>
          <div className="min-h-[22px]" key={fileId || 'message-0'}>
            {MessageFactory({ ...item, isUser })}
          </div>
        </MessageImagesProvider>
      );
    }

    return (
      <MessageImagesProvider images={imageItems}>
        <div
          className={cn('flex flex-col', nonPdfItemsLength > 0 && pdfItems.length > 0 && 'gap-4')}
        >
          {/* Render images/other media in a grid */}
          {nonPdfItemsLength > 0 && (
            <div
              className={cn(
                'grid gap-2 max-w-full justify-center',
                nonPdfItemsLength === 2 ? 'grid-cols-2' : 'grid-cols-2 sm:grid-cols-3',
                nonPdfItemsLength > 4 && 'grid-cols-2 sm:grid-cols-1 md:grid-cols-3',
              )}
            >
              {nonPdfItems.map((item, index) => {
                const fileId = item.file_id || item.url;
                return (
                  <div
                    key={fileId || `grid-item-${index}`}
                    className="min-h-[22px] flex flex-col justify-center md:justify-start"
                  >
                    <div className="w-full bg-white rounded-lg overflow-hidden">
                      <div>{MessageFactory({ ...item, isUser })}</div>
                      {item.label && (
                        <div className="w-full text-xs p-2 text-center text-gray-700 font-medium break-words">
                          {item.label}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Render PDFs in a list */}
          {pdfItems.length > 0 && (
            <div className="flex flex-col gap-2 w-full">
              {pdfItems.map((item, index) => {
                const fileId = item.file_id || item.url;
                return (
                  <div key={fileId || `pdf-item-${index}`} className="w-full">
                    {MessageFactory({ ...item, isUser })}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </MessageImagesProvider>
    );
  };

  return (
    <div
      className={cn(
        'flex w-full self-end gap-3 md700:gap-1.5 max-w-[85%] min450:max-w-full transition-all',
        isUser ? 'self-end' : 'self-start',
      )}
    >
      <div className={cn('flex flex-col w-full', isUser && 'items-end w-full')}>
        <motion.div
          style={{
            backgroundColor,
            color: textColor,
          }}
          className={cn(
            'w-fit px-3 py-2.5 font-medium md700:ml-0 text-[15px] shadow-md max-w-[85%] min450:max-w-[95%] relative md600:ml-0 rounded-t-xl',
            hasError && '!bg-red-400 border !text-white',
            data &&
              data.length > 0 &&
              data.some((item) => item.url) &&
              !!text &&
              'flex flex-col gap-4',
            isUser ? 'rounded-l-xl' : 'rounded-br-xl',
          )}
          initial={{ opacity: 0.5, scale: 0.95, filter: 'blur(3px)' }}
          animate={{ opacity: 1, scale: 1, filter: 'blur(0px)' }}
          transition={{ duration: 0.3, ease: 'easeOut' }}
        >
          {/* Show loading animation if the message is still being sent */}
          {loading && (
            <div className="px-4 h-[22px] flex items-center">
              <ThreeDotLoader />
            </div>
          )}

          <MessageContent
            isUser={isUser}
            isLastMessage={isLastMessage}
            loading={loading}
            sanitizedFormattedText={sanitizedFormattedText}
            sanitizedDisplayResponse={sanitizedDisplayResponse}
          />

          {/* Conditional rendering for messages based on user and message state */}
          {(isUser || (!isUser && (isLastMessageDisplayed || !isLastMessage))) && (
            <div>{renderMessages()}</div>
          )}
        </motion.div>

        <MessageTimestamp
          date={formatDate(created_at, 'DD.MMM')}
          time={formatDate(created_at, 'hh:mm a')}
          {...{ isUser, seen, received }}
        />
      </div>
    </div>
  );
}

export default TextMessage;
